import React, { useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Slider,
  Button,
  Chip,
  Stack,
  Collapse,
  TextField,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Close,
  FilterList,
  ExpandMore,
  ExpandLess,
  RestartAlt,
  CalendarToday,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import { FilterOptions, MedicalDataFile } from '../../types/medicalDataList';
import GlassCard from '../common/GlassCard';

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filterOptions: FilterOptions;
  onFilterChange: (options: Partial<FilterOptions>) => void;
  onReset: () => void;
}

const statusOptions = [
  { value: 'uploaded', label: 'アップロード済', color: 'default' },
  { value: 'processing', label: '処理中', color: 'warning' },
  { value: 'completed', label: '完了', color: 'success' },
  { value: 'error', label: 'エラー', color: 'error' },
] as const;

const tagOptions = ['医療', '診療', '予約', '患者', 'レポート', '分析'];

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  filterOptions,
  onFilterChange,
  onReset,
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    status: true,
    date: true,
    size: true,
    tags: true,
  });

  const [fileSizeRange, setFileSizeRange] = useState<[number, number]>([0, 10]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleStatusChange = (status: MedicalDataFile['status']) => {
    const currentStatuses = filterOptions.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter(s => s !== status)
      : [...currentStatuses, status];
    
    onFilterChange({ status: newStatuses.length > 0 ? newStatuses : undefined });
  };

  const handleFileSizeChange = (event: Event, newValue: number | number[]) => {
    const range = newValue as [number, number];
    setFileSizeRange(range);
    onFilterChange({
      fileSizeRange: {
        min: range[0] * 1024 * 1024, // MB to bytes
        max: range[1] * 1024 * 1024,
      },
    });
  };

  const handleStartDateChange = (newValue: Date | null) => {
    setStartDate(newValue);
    if (newValue && endDate) {
      onFilterChange({
        dateRange: {
          start: newValue,
          end: endDate,
        },
      });
    }
  };

  const handleEndDateChange = (newValue: Date | null) => {
    setEndDate(newValue);
    if (startDate && newValue) {
      onFilterChange({
        dateRange: {
          start: startDate,
          end: newValue,
        },
      });
    }
  };

  const handleTagToggle = (tag: string) => {
    const newTags = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    
    setSelectedTags(newTags);
    onFilterChange({ tags: newTags.length > 0 ? newTags : undefined });
  };

  const handleReset = () => {
    setFileSizeRange([0, 10]);
    setStartDate(null);
    setEndDate(null);
    setSelectedTags([]);
    onReset();
  };

  const formatFileSize = (mb: number) => `${mb} MB`;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 360,
          background: alpha(theme.palette.background.paper, 0.95),
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList color="primary" />
            <Typography variant="h6" fontWeight="bold">
              フィルター
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Status Filter */}
        <GlassCard sx={{ mb: 2, p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={() => toggleSection('status')}
          >
            <Typography variant="subtitle1" fontWeight="600">
              ステータス
            </Typography>
            <IconButton size="small">
              {expandedSections.status ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={expandedSections.status}>
            <FormGroup sx={{ mt: 2 }}>
              {statusOptions.map(option => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={filterOptions.status?.includes(option.value) || false}
                      onChange={() => handleStatusChange(option.value)}
                      color="primary"
                    />
                  }
                  label={
                    <Chip
                      label={option.label}
                      size="small"
                      color={option.color as any}
                      variant={filterOptions.status?.includes(option.value) ? 'filled' : 'outlined'}
                    />
                  }
                />
              ))}
            </FormGroup>
          </Collapse>
        </GlassCard>

        {/* Date Range Filter */}
        <GlassCard sx={{ mb: 2, p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={() => toggleSection('date')}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday fontSize="small" />
              <Typography variant="subtitle1" fontWeight="600">
                日付範囲
              </Typography>
            </Box>
            <IconButton size="small">
              {expandedSections.date ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={expandedSections.date}>
            <Box sx={{ mt: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
                <DatePicker
                  label="開始日"
                  value={startDate}
                  onChange={handleStartDateChange}
                  slots={{
                    textField: TextField,
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: { mb: 2 },
                    },
                  }}
                />
                <DatePicker
                  label="終了日"
                  value={endDate}
                  onChange={handleEndDateChange}
                  minDate={startDate || undefined}
                  slots={{
                    textField: TextField,
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Collapse>
        </GlassCard>

        {/* File Size Filter */}
        <GlassCard sx={{ mb: 2, p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={() => toggleSection('size')}
          >
            <Typography variant="subtitle1" fontWeight="600">
              ファイルサイズ
            </Typography>
            <IconButton size="small">
              {expandedSections.size ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={expandedSections.size}>
            <Box sx={{ mt: 2, px: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {formatFileSize(fileSizeRange[0])} - {formatFileSize(fileSizeRange[1])}
              </Typography>
              <Slider
                value={fileSizeRange}
                onChange={handleFileSizeChange}
                valueLabelDisplay="auto"
                valueLabelFormat={formatFileSize}
                min={0}
                max={50}
                marks={[
                  { value: 0, label: '0' },
                  { value: 10, label: '10' },
                  { value: 25, label: '25' },
                  { value: 50, label: '50MB' },
                ]}
              />
            </Box>
          </Collapse>
        </GlassCard>

        {/* Tags Filter */}
        <GlassCard sx={{ mb: 3, p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
            }}
            onClick={() => toggleSection('tags')}
          >
            <Typography variant="subtitle1" fontWeight="600">
              タグ
            </Typography>
            <IconButton size="small">
              {expandedSections.tags ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={expandedSections.tags}>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
              {tagOptions.map(tag => (
                <motion.div
                  key={tag}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Chip
                    label={tag}
                    onClick={() => handleTagToggle(tag)}
                    color={selectedTags.includes(tag) ? 'primary' : 'default'}
                    variant={selectedTags.includes(tag) ? 'filled' : 'outlined'}
                    sx={{ cursor: 'pointer' }}
                  />
                </motion.div>
              ))}
            </Stack>
          </Collapse>
        </GlassCard>

        {/* Action Buttons */}
        <Stack direction="row" spacing={2}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<RestartAlt />}
            onClick={handleReset}
            sx={{
              borderColor: alpha(theme.palette.divider, 0.3),
              '&:hover': {
                borderColor: theme.palette.divider,
                backgroundColor: alpha(theme.palette.action.hover, 0.05),
              },
            }}
          >
            リセット
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={onClose}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}
          >
            適用
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;